<h1 mat-dialog-title>{{isNew ? 'Create organisation' : 'Organisation settings'}}</h1>
<form [formGroup]="organisationForm" (ngSubmit)="save()" autocomplete="off">
  <mat-dialog-content>
    @if (organisation.isPartner || organisation.partnerName) {
      <div class="badges">
        @if (organisation.isPartner) {
          <span class="badge"><mat-icon>handshake</mat-icon>LINQ partner</span>
        }
        @if (organisation.partnerName) {
          <span class="badge">Set up by {{organisation.partnerName}}</span>
        }
      </div>
    }

    <section>
      <h2 class="section-title">Details</h2>
      <mat-form-field color="accent">
        <mat-label>Name</mat-label>
        <input type="text" placeholder="The company name" matInput formControlName="name" [errorStateMatcher]="matcher" autocomplete="organization" id="organisation-name" cdkFocusInitial>
        @if (organisationForm.controls['name'].hasError('required')) {
          <mat-error>Name is <strong>required</strong></mat-error>
        }@else if (organisationForm.controls['name'].hasError('minlength') || organisationForm.controls['name'].hasError('maxlength')) {
          <mat-error>Name must be between {{nameMin}} and {{nameMax}} characters</mat-error>
        }
      </mat-form-field>
      <div class="row">
        <mat-form-field>
          <mat-label>Industry</mat-label>
          <mat-select formControlName="industry" [errorStateMatcher]="matcher">
            @for (industry of industries; track industry) {
              <mat-option [value]="industry">{{industry.toString()}}</mat-option>
            }
          </mat-select>
          @if (organisationForm.controls['industry'].invalid) {
            <mat-error>Please select an industry</mat-error>
          }
        </mat-form-field>
        @if (isSystemAdminUser()) {
          <mat-form-field>
            <mat-label>Expiry date</mat-label>
            <input matInput [matDatepicker]="picker" formControlName="endDate">
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            <mat-hint>Leave blank for no expiry</mat-hint>
          </mat-form-field>
        }
      </div>
      @if (!isNew) {
        <p class="note">Created {{createdDate}}</p>
      }
    </section>

    <section>
      <h2 class="section-title">Billing</h2>
      <mat-form-field>
        <mat-label>Currency</mat-label>
        <mat-select formControlName="currencyCode" [errorStateMatcher]="matcher">
          @for (currency of currencies; track currency.code) {
            <mat-option [value]="currency.code">{{currency.code}} - {{currency.name}}</mat-option>
          }
        </mat-select>
        <mat-hint>Costs, revenue and invoices are shown in this currency</mat-hint>
        @if (organisationForm.controls['currencyCode'].invalid) {
          <mat-error>Please select a currency</mat-error>
        }
      </mat-form-field>
      <div class="row">
        <mat-form-field color="accent">
          <mat-label>Billing email</mat-label>
          <input type="email" placeholder="accounts@example.com" matInput formControlName="billingEmail" [errorStateMatcher]="matcher" autocomplete="email">
          <mat-hint>Where invoices are sent</mat-hint>
          @if (organisationForm.controls['billingEmail'].hasError('email')) {
            <mat-error>Enter a valid email address</mat-error>
          }@else if (organisationForm.controls['billingEmail'].hasError('maxlength')) {
            <mat-error>No more than {{billingEmailMax}} characters</mat-error>
          }
        </mat-form-field>
        <mat-form-field color="accent">
          <mat-label>Invoice reference</mat-label>
          <input type="text" placeholder="e.g. a purchase order number" matInput formControlName="invoiceReference" [errorStateMatcher]="matcher">
          <mat-hint>Shown on each invoice</mat-hint>
          @if (organisationForm.controls['invoiceReference'].hasError('maxlength')) {
            <mat-error>No more than {{invoiceReferenceMax}} characters</mat-error>
          }
        </mat-form-field>
      </div>
    </section>

    <section>
      <h2 class="section-title">Capacity</h2>
      <div class="row capacity">
        <mat-form-field color="accent">
          <mat-label>Full-time hours a year</mat-label>
          <input type="number" matInput formControlName="defaultCapacityHours" min="1" [max]="maxHours" [placeholder]="'' + suggestedHours" [errorStateMatcher]="matcher" data-testid="default-capacity-hours">
          @if (organisationForm.controls['defaultCapacityHours'].invalid) {
            <mat-error>Between 1 and {{maxHours}} hours</mat-error>
          }
        </mat-form-field>
        @if (!organisationForm.value.defaultCapacityHours) {
          <button mat-stroked-button type="button" (click)="useSuggestedHours()">Use {{suggestedHours | number}}</button>
        }
      </div>
      <p class="note">The hours one full-time person works in a year, used for people and roles whose capacity isn't set
        (a role, for each of its people). Typically {{suggestedHours | number}}: 40 hours a week for 46 weeks, after leave
        and public holidays. Leave it empty to have their capacity match their work, which says nothing about workload.</p>
    </section>

    <section>
      <h2 class="section-title">Missing data warnings</h2>
      <p class="note">Turn off the warnings for data this organisation doesn't model, so models aren't flagged for leaving it out.</p>
      <div class="switch-grid">
        <mat-slide-toggle formControlName="ignoreCost" color="accent">Hide cost warnings</mat-slide-toggle>
        <mat-slide-toggle formControlName="ignoreCarbon" color="accent">Hide carbon warnings</mat-slide-toggle>
        <mat-slide-toggle formControlName="ignoreCapacity" color="accent">Hide capacity warnings</mat-slide-toggle>
        <mat-slide-toggle formControlName="ignoreValue" color="accent">Hide value warnings</mat-slide-toggle>
      </div>
    </section>

    @if (isSystemAdminUser()) {
      <section class="admin">
        <h2 class="section-title"><mat-icon>admin_panel_settings</mat-icon>LINQ admin</h2>
        <div class="switch-grid">
          <mat-slide-toggle formControlName="isPartner" color="accent">Partner</mat-slide-toggle>
          <mat-slide-toggle formControlName="isCustomer" color="accent">Customer</mat-slide-toggle>
          <mat-slide-toggle formControlName="isExemptFromPayment" color="accent">Exempt from payment</mat-slide-toggle>
        </div>
        <mat-form-field>
          <mat-label>Xero contact ID</mat-label>
          <input type="text" matInput formControlName="contactId" [errorStateMatcher]="matcher">
          @if (organisationForm.controls['contactId'].hasError('maxlength')) {
            <mat-error>No more than {{contactIdMax}} characters</mat-error>
          }
        </mat-form-field>
      </section>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button type="button" (click)="onCancelClick()">Cancel</button>
    <button mat-flat-button color="accent" type="submit">{{isNew ? 'Create' : 'Save'}}</button>
  </mat-dialog-actions>
</form>
